
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, MapPin, Clock, Video, Globe, DollarSign, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';
import { useCityTranslations } from '@/hooks/useCityTranslations';
import { publicTherapistService } from '@/services/publicTherapistService';
import { generateMockTherapists } from '@/data/mockTherapists';

interface CityTherapistSectionProps {
  city: City;
  language?: CityLanguage;
  specialtyFilter?: string;
}

const CityTherapistSection: React.FC<CityTherapistSectionProps> = ({ 
  city, 
  language = 'en',
  specialtyFilter
}) => {
  // Validate city data before rendering
  if (!city || !city.name) {
    return (
      <div className="py-12 bg-background">
        <div className="container text-center">
          <p className="text-muted-foreground">
            Loading therapist information...
          </p>
        </div>
      </div>
    );
  }

  const [therapists, setTherapists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const { translations } = useCityTranslations(city, language);
  const cleanCityName = city.name.replace(/\s+\d+$/, '').trim();

  const getTranslation = (key: string, fallback: string = '') => {
    return translations[key] || fallback;
  };

  useEffect(() => {
    const fetchTherapists = async () => {
      setLoading(true);
      const { data, error } = await publicTherapistService.getTherapistsByCity(
        cleanCityName,
        city.country
      );
      
      if (!error && data && data.length > 0) {
        setTherapists(data);
      } else {
        // Use mock data when no real therapists are available
        const mockData = generateMockTherapists(cleanCityName, city.country, 15);
        setTherapists(mockData);
      }
      setLoading(false);
    };

    fetchTherapists();
  }, [cleanCityName, city.country]);

  if (loading) {
    return (
      <div className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(therapists.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTherapists = therapists.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              Top Therapists in {cleanCityName}
            </h2>
            <p className="text-lg text-muted-foreground">
              {therapists.length} verified mental health professionals in your area
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {currentTherapists.map((therapist) => {
              const profile = therapist.profile || {};
              const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Professional';
              const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase();
              const specialties = therapist.therapist_specialties?.map((ts: any) => ts.specialty?.name).filter(Boolean) || [];
              
              return (
                <Card key={therapist.id} className="hover:shadow-lg transition-shadow border-border">
                  <CardHeader className="pb-3">
                    {therapist.is_online_available && (
                      <Badge className="bg-green-500 text-white mb-2 w-fit">
                        <Clock className="h-3 w-3 mr-1" />
                        Available Online
                      </Badge>
                    )}
                    
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={profile.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${fullName}`} />
                          <AvatarFallback className="bg-brand-blue-100 text-brand-blue-600">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base leading-tight mb-1">{fullName}</CardTitle>
                          <p className="text-sm text-brand-blue-600 truncate">
                            {specialties[0] || 'Mental Health Professional'}
                          </p>
                        </div>
                      </div>
                      {therapist.rating > 0 && (
                        <div className="flex items-center gap-1 ml-2">
                          <Star className="h-4 w-4 text-yellow-500 fill-current flex-shrink-0" />
                          <span className="font-medium text-sm">{therapist.rating}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{therapist.city}, {therapist.country}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Video className="h-4 w-4 flex-shrink-0" />
                        <span>
                          {therapist.is_online_available && therapist.is_in_person_available 
                            ? 'Online & In-Person' 
                            : therapist.is_online_available 
                            ? 'Online' 
                            : 'In-Person'}
                        </span>
                      </div>
                      {therapist.languages && therapist.languages.length > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Globe className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{therapist.languages.join(', ')}</span>
                        </div>
                      )}
                      {therapist.hourly_rate && (
                        <div className="flex items-center gap-2 font-semibold text-brand-blue-600">
                          <DollarSign className="h-4 w-4 flex-shrink-0" />
                          <span>{therapist.currency || 'USD'} {therapist.hourly_rate}/session</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button className="flex-1 bg-brand-blue-600 hover:bg-brand-blue-700" size="sm">
                        Book Now
                      </Button>
                      <Button variant="outline" size="sm" className="border-border">
                        Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className={currentPage === page ? "bg-brand-blue-600 hover:bg-brand-blue-700" : ""}
                  >
                    {page}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="text-center bg-brand-blue-50 p-8 rounded-lg">
            <p className="text-lg text-brand-gray-700 mb-4">
              {language === 'es' 
                ? `Mostrando ${startIndex + 1}-${Math.min(endIndex, therapists.length)} de ${therapists.length} terapeutas en ${cleanCityName}`
                : `Showing ${startIndex + 1}-${Math.min(endIndex, therapists.length)} of ${therapists.length} therapists in ${cleanCityName}`}
            </p>
            <Button size="lg" className="bg-brand-blue-600 hover:bg-brand-blue-700">
              <Search className="h-5 w-5 mr-2" />
              {language === 'es' 
                ? `Ver todos los terapeutas en ${cleanCityName}`
                : `View All Therapists in ${cleanCityName}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityTherapistSection;
